import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Plus, Save, Trash2, Pencil, Euro, CheckCircle2, Star, Building2, FileCheck2, Globe2,
  Receipt, AlarmClockOff, AlertTriangle, ShieldCheck, BadgeEuro, Sparkles, Rocket, Copy,
} from "lucide-react";
import { toast } from "sonner";

const cents = (v) => (Number(v || 0) / 100).toFixed(2) + " €";

// =========================================================
// AGENCY DASHBOARD (KPIs)
// =========================================================
export function AgencyDashboardAdmin() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/agency/dashboard");
      setD(r.data);
    } catch (e) { toast.error("Chargement dashboard impossible"); }
  };
  const runCycle = async () => {
    setLoading(true);
    try {
      const r = await api.post("/agency/suspension/run-now");
      toast.success(`Cycle exécuté — J0 : ${r.data.passed_to_past_due} · J+7 : ${r.data.passed_to_suspended}`);
      load();
    } catch { toast.error("Échec du cycle"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!d) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  const kpis = [
    { label: "Clients", value: d.clients_count, icon: Building2, color: "#60A5FA" },
    { label: "Abonnements actifs", value: d.active_subscriptions, icon: CheckCircle2, color: "#34D399" },
    { label: "Impayés (J0→J+7)", value: d.past_due_subscriptions, icon: AlertTriangle, color: "#F59E0B" },
    { label: "Suspendus", value: d.suspended_subscriptions, icon: AlarmClockOff, color: "#EF4444" },
    { label: "En attente d'onboarding", value: d.pending_onboarding_requests, icon: Sparkles, color: "#A78BFA" },
    { label: "MRR estimé", value: cents(d.mrr_cents), icon: BadgeEuro, color: "#22D3EE", isString: true },
  ];

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-dashboard-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Tableau de bord Accès Agence</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">KPIs globaux du parc clients & état des paiements.</p>
        </div>
        <Button onClick={runCycle} disabled={loading} data-testid="agency-run-cycle-btn">
          {loading ? "En cours…" : "Exécuter le cycle J0 / J+7 maintenant"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map(k => (
          <Card key={k.label} className="p-4 flex flex-col gap-1" style={{ borderLeft: `4px solid ${k.color}` }}>
            <div className="flex items-center gap-2 text-xs uppercase text-[hsl(var(--muted-foreground))]">
              <k.icon className="w-4 h-4" style={{ color: k.color }} /> {k.label}
            </div>
            <div className="text-2xl font-bold">{k.value ?? 0}</div>
          </Card>
        ))}
      </div>

      {d.domains_expiring_soon?.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-amber-500" />
            <h3 className="font-bold text-sm">Domaines expirant dans les 30 jours</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-[hsl(var(--muted-foreground))] border-b">
                <th className="text-left py-2">Domaine</th>
                <th className="text-left py-2">Registrar</th>
                <th className="text-left py-2">Expire le</th>
              </tr>
            </thead>
            <tbody>
              {d.domains_expiring_soon.map(dom => (
                <tr key={dom.id} className="border-b last:border-0">
                  <td className="py-2 font-mono">{dom.domain}</td>
                  <td className="py-2">{dom.registrar || "—"}</td>
                  <td className="py-2">{(dom.expires_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// =========================================================
// PRICING PLANS (forfaits)
// =========================================================
const blankPlan = {
  code: "", name: "", description: "",
  monthly_price_cents: 0, setup_fee_cents: 0, setup_fee_installments_max: 3,
  included_widgets: [], trial_days: 0, max_products: 0, max_bookings_per_month: 0,
  ai_credits_monthly: 0, support_hours_monthly: 0, is_active: true, sort_order: 100,
};

export function AgencyPricingAdmin() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/agency/pricing-plans");
    setPlans(r.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ ...blankPlan });
  const openEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.code || !editing.name) { toast.error("Code et Nom requis"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.put(`/agency/pricing-plans/${editing.id}`, payload);
      else await api.post("/agency/pricing-plans", payload);
      toast.success("Forfait enregistré");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce forfait ?")) return;
    await api.delete(`/agency/pricing-plans/${id}`);
    load();
    toast.success("Forfait supprimé");
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-pricing-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Forfaits Agence</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Configure les offres proposées aux clients lors de l'onboarding.</p>
        </div>
        <Button onClick={openCreate} data-testid="agency-pricing-create-btn"><Plus className="w-4 h-4 mr-1" />Nouveau forfait</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <Card key={p.id} className="p-5 flex flex-col gap-3" data-testid={`pricing-plan-card-${p.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase text-[hsl(var(--muted-foreground))]">{p.code}</div>
                <div className="text-xl font-bold">{p.name}</div>
              </div>
              {p.is_active ? <Badge className="bg-emerald-500">Actif</Badge> : <Badge variant="secondary">Inactif</Badge>}
            </div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">{p.description}</div>
            <div className="flex gap-4 text-sm">
              <div><span className="text-[hsl(var(--muted-foreground))]">Mensuel :</span> <strong>{cents(p.monthly_price_cents)}</strong></div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Setup :</span> <strong>{cents(p.setup_fee_cents)}</strong> ({p.setup_fee_installments_max}×)</div>
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              Inclus : {p.included_widgets?.length || 0} widgets · Essai {p.trial_days} j · IA {p.ai_credits_monthly} crédits · Support {p.support_hours_monthly} h
            </div>
            <div className="flex gap-2 mt-auto pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => openEdit(p)}><Pencil className="w-3 h-3 mr-1" />Éditer</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(p.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={o => !o && cancel()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le forfait" : "Nouveau forfait"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value })} placeholder="starter" data-testid="pricing-code" />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Starter" data-testid="pricing-name" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} rows={2} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Prix mensuel (cts)</Label>
                  <Input type="number" value={editing.monthly_price_cents} onChange={e => setEditing({ ...editing, monthly_price_cents: parseInt(e.target.value || 0) })} />
                </div>
                <div>
                  <Label>Frais de setup (cts)</Label>
                  <Input type="number" value={editing.setup_fee_cents} onChange={e => setEditing({ ...editing, setup_fee_cents: parseInt(e.target.value || 0) })} />
                </div>
                <div>
                  <Label>Max échéances setup</Label>
                  <Input type="number" min={1} max={4} value={editing.setup_fee_installments_max} onChange={e => setEditing({ ...editing, setup_fee_installments_max: Math.max(1, Math.min(4, parseInt(e.target.value || 1))) })} />
                </div>
              </div>
              <div>
                <Label>Widgets inclus (clés séparées par virgule)</Label>
                <Input
                  value={(editing.included_widgets || []).join(", ")}
                  onChange={e => setEditing({ ...editing, included_widgets: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })}
                  placeholder="shop_enabled, booking_enabled, wishlist_enabled"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label>Essai (j)</Label>
                  <Input type="number" value={editing.trial_days} onChange={e => setEditing({ ...editing, trial_days: parseInt(e.target.value || 0) })} />
                </div>
                <div>
                  <Label>Max produits</Label>
                  <Input type="number" value={editing.max_products} onChange={e => setEditing({ ...editing, max_products: parseInt(e.target.value || 0) })} />
                </div>
                <div>
                  <Label>IA crédits/mois</Label>
                  <Input type="number" value={editing.ai_credits_monthly} onChange={e => setEditing({ ...editing, ai_credits_monthly: parseInt(e.target.value || 0) })} />
                </div>
                <div>
                  <Label>Support (h/mois)</Label>
                  <Input type="number" value={editing.support_hours_monthly} onChange={e => setEditing({ ...editing, support_hours_monthly: parseInt(e.target.value || 0) })} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.is_active} onCheckedChange={v => setEditing({ ...editing, is_active: v })} />
                <Label>Forfait actif (visible onboarding)</Label>
                <div className="ml-auto">
                  <Label className="text-xs">Ordre tri</Label>
                  <Input type="number" className="w-20 inline-block ml-2" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value || 0) })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancel}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="pricing-save-btn"><Save className="w-4 h-4 mr-1" />{saving ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================
// WIDGET CATALOG
// =========================================================
const blankWidget = {
  widget_key: "", label: "", category: "shop", description: "",
  is_payable: false, monthly_price_cents: 0, discount_pct: 0,
  discount_ends_at: null, trial_days: 0, is_active: true,
};

export function AgencyWidgetCatalogAdmin() {
  const [widgets, setWidgets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/agency/widget-catalog");
    setWidgets(r.data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const m = {};
    widgets.forEach(w => { m[w.category] = [...(m[w.category] || []), w]; });
    return m;
  }, [widgets]);

  const save = async () => {
    if (!editing.widget_key || !editing.label) { toast.error("Clé et Label requis"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.put(`/agency/widget-catalog/${editing.id}`, payload);
      else await api.post("/agency/widget-catalog", payload);
      toast.success("Widget enregistré");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce widget du catalogue ?")) return;
    await api.delete(`/agency/widget-catalog/${id}`);
    load(); toast.success("Widget retiré");
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-widget-catalog-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Catalogue widgets</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Définis les widgets disponibles, leur prix et les remises/essais.</p>
        </div>
        <Button onClick={() => setEditing({ ...blankWidget })} data-testid="widget-create-btn"><Plus className="w-4 h-4 mr-1" />Nouveau widget</Button>
      </div>

      {Object.entries(grouped).map(([cat, list]) => (
        <Card key={cat} className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Badge>{cat}</Badge>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">{list.length} widget(s)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map(w => (
              <Card key={w.id} className="p-3 flex flex-col gap-2" data-testid={`widget-card-${w.widget_key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{w.widget_key}</div>
                    <div className="font-bold text-sm">{w.label}</div>
                  </div>
                  {w.is_payable && <Badge className="bg-amber-500">Payant</Badge>}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{w.description}</div>
                <div className="flex items-center gap-3 text-xs">
                  {w.is_payable && <span><Euro className="w-3 h-3 inline" /> {cents(w.monthly_price_cents)}/mois</span>}
                  {w.discount_pct > 0 && <Badge variant="outline" className="text-red-500 border-red-500">-{w.discount_pct}%</Badge>}
                  {w.trial_days > 0 && <Badge variant="outline">{w.trial_days} j essai</Badge>}
                </div>
                <div className="flex gap-1 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => setEditing({ ...w })}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(w.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      ))}

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le widget" : "Nouveau widget"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Clé (widget_key)</Label>
                  <Input value={editing.widget_key} onChange={e => setEditing({ ...editing, widget_key: e.target.value })} placeholder="loyalty_enabled" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={editing.category} onValueChange={v => setEditing({ ...editing, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["shop","booking","clients","contenu","marketing"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Label affiché</Label>
                <Input value={editing.label} onChange={e => setEditing({ ...editing, label: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.is_payable} onCheckedChange={v => setEditing({ ...editing, is_payable: v })} />
                <Label>Widget payant (facturation en option)</Label>
              </div>
              {editing.is_payable && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Prix mensuel (cts)</Label>
                    <Input type="number" value={editing.monthly_price_cents} onChange={e => setEditing({ ...editing, monthly_price_cents: parseInt(e.target.value || 0) })} />
                  </div>
                  <div>
                    <Label>Remise (%)</Label>
                    <Input type="number" value={editing.discount_pct} onChange={e => setEditing({ ...editing, discount_pct: parseFloat(e.target.value || 0) })} />
                  </div>
                  <div>
                    <Label>Essai (j)</Label>
                    <Input type="number" value={editing.trial_days} onChange={e => setEditing({ ...editing, trial_days: parseInt(e.target.value || 0) })} />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Switch checked={editing.is_active} onCheckedChange={v => setEditing({ ...editing, is_active: v })} />
                <Label>Actif dans le catalogue</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving}><Save className="w-4 h-4 mr-1" />{saving ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================
// CGV VERSIONS
// =========================================================
const blankCGV = {
  version: "", title: "Conditions Générales de Vente",
  content_html: "", effective_from: new Date().toISOString().slice(0, 10),
};

export function AgencyCGVAdmin() {
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/agency/cgv-versions");
    setVersions(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.version || !editing.content_html) { toast.error("Version et contenu requis"); return; }
    setSaving(true);
    try {
      const payload = {
        version: editing.version, title: editing.title,
        content_html: editing.content_html, effective_from: editing.effective_from,
      };
      if (editing.id) await api.put(`/agency/cgv-versions/${editing.id}`, payload);
      else await api.post("/agency/cgv-versions", payload);
      toast.success("CGV enregistrées");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const publish = async (id) => {
    if (!window.confirm("Publier cette version ? L'ancienne version sera archivée.")) return;
    await api.post(`/agency/cgv-versions/${id}/publish`);
    toast.success("Version publiée"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette version ?")) return;
    try { await api.delete(`/agency/cgv-versions/${id}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-cgv-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">CGV Agence</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Gère les versions successives des Conditions Générales de Vente. Le client signe la version courante à l'onboarding.</p>
        </div>
        <Button onClick={() => setEditing({ ...blankCGV })} data-testid="cgv-create-btn"><Plus className="w-4 h-4 mr-1" />Nouvelle version</Button>
      </div>

      <div className="space-y-3">
        {versions.map(v => (
          <Card key={v.id} className="p-4 flex items-center gap-3" data-testid={`cgv-row-${v.version}`}>
            <FileCheck2 className={`w-6 h-6 ${v.is_current ? "text-emerald-500" : "text-gray-400"}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <strong>{v.version}</strong>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">· {v.title}</span>
                {v.is_current && <Badge className="bg-emerald-500">Version courante</Badge>}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">En vigueur depuis : {(v.effective_from || "").slice(0, 10)}</div>
            </div>
            {!v.is_current && <Button size="sm" variant="outline" onClick={() => publish(v.id)} data-testid={`cgv-publish-${v.version}`}>Publier</Button>}
            <Button size="sm" variant="outline" onClick={() => setEditing({ ...v })}><Pencil className="w-3 h-3" /></Button>
            <Button size="sm" variant="ghost" onClick={() => remove(v.id)} disabled={v.is_current}><Trash2 className="w-3 h-3 text-red-500" /></Button>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier la version CGV" : "Nouvelle version CGV"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Version</Label>
                  <Input value={editing.version} onChange={e => setEditing({ ...editing, version: e.target.value })} placeholder="v2.0" disabled={!!editing.id} />
                </div>
                <div className="col-span-2">
                  <Label>Titre</Label>
                  <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>En vigueur depuis</Label>
                <Input type="date" value={(editing.effective_from || "").slice(0, 10)} onChange={e => setEditing({ ...editing, effective_from: e.target.value })} />
              </div>
              <div>
                <Label>Contenu HTML</Label>
                <Textarea rows={18} value={editing.content_html} onChange={e => setEditing({ ...editing, content_html: e.target.value })} className="font-mono text-xs" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving}><Save className="w-4 h-4 mr-1" />{saving ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================
// AGENCY CLIENTS
// =========================================================
const blankClient = {
  company_name: "", legal_name: "", siret: "", vat_number: "",
  contact_email: "", contact_first_name: "", contact_last_name: "", contact_phone: "",
  billing_address: "", billing_zip: "", billing_city: "", billing_country: "FR",
  sector: "", status: "prospect", notes: "", tags: [],
  site_id: null, site_url: null, contracts: [],
};

export function AgencyClientsAdmin() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);   // détail
  const [search, setSearch] = useState("");

  const load = async () => {
    const r = await api.get("/agency/clients");
    setClients(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      (c.company_name || "").toLowerCase().includes(q) ||
      (c.contact_email || "").toLowerCase().includes(q) ||
      (c.sector || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  const save = async () => {
    if (!editing.company_name || !editing.contact_email) { toast.error("Nom entreprise & email requis"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.subscription; delete payload.domain;
      if (editing.id) await api.put(`/agency/clients/${editing.id}`, payload);
      else await api.post("/agency/clients", payload);
      toast.success("Client enregistré"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce client et ses abonnements / domaines liés ?")) return;
    await api.delete(`/agency/clients/${id}`);
    load(); toast.success("Client supprimé");
  };

  const statusColor = { prospect: "#94A3B8", onboarding: "#60A5FA", active: "#34D399", paused: "#F59E0B", archived: "#64748B" };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-clients-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Clients Agence</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Fiche centrale de chaque client : coordonnées, abonnement, domaine, contrats.</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} className="w-64" />
          <Button onClick={() => setEditing({ ...blankClient })} data-testid="agency-client-create-btn"><Plus className="w-4 h-4 mr-1" />Nouveau client</Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-xs uppercase">
            <tr>
              <th className="text-left p-3">Entreprise</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Secteur</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center p-10 text-[hsl(var(--muted-foreground))]">Aucun client — commence par en créer un.</td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} className="border-t hover:bg-[hsl(var(--muted))]/30" data-testid={`client-row-${c.id}`}>
                <td className="p-3 font-medium">{c.company_name}</td>
                <td className="p-3">
                  <div>{c.contact_first_name} {c.contact_last_name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{c.contact_email}</div>
                </td>
                <td className="p-3">{c.sector || "—"}</td>
                <td className="p-3"><Badge style={{ backgroundColor: statusColor[c.status] || "#94A3B8" }}>{c.status}</Badge></td>
                <td className="p-3 flex gap-1">
                  <Button size="sm" variant="outline" onClick={async () => {
                    const r = await api.get(`/agency/clients/${c.id}`); setSelected(r.data);
                  }}>Voir</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le client" : "Nouveau client"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Entreprise *</Label><Input value={editing.company_name} onChange={e => setEditing({ ...editing, company_name: e.target.value })} /></div>
                <div><Label>Raison sociale</Label><Input value={editing.legal_name} onChange={e => setEditing({ ...editing, legal_name: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>SIRET</Label><Input value={editing.siret} onChange={e => setEditing({ ...editing, siret: e.target.value })} /></div>
                <div><Label>TVA intra</Label><Input value={editing.vat_number} onChange={e => setEditing({ ...editing, vat_number: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom contact</Label><Input value={editing.contact_first_name} onChange={e => setEditing({ ...editing, contact_first_name: e.target.value })} /></div>
                <div><Label>Nom contact</Label><Input value={editing.contact_last_name} onChange={e => setEditing({ ...editing, contact_last_name: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email *</Label><Input type="email" value={editing.contact_email} onChange={e => setEditing({ ...editing, contact_email: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input value={editing.contact_phone} onChange={e => setEditing({ ...editing, contact_phone: e.target.value })} /></div>
              </div>
              <div><Label>Adresse facturation</Label><Input value={editing.billing_address} onChange={e => setEditing({ ...editing, billing_address: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>CP</Label><Input value={editing.billing_zip} onChange={e => setEditing({ ...editing, billing_zip: e.target.value })} /></div>
                <div><Label>Ville</Label><Input value={editing.billing_city} onChange={e => setEditing({ ...editing, billing_city: e.target.value })} /></div>
                <div><Label>Pays</Label><Input value={editing.billing_country} onChange={e => setEditing({ ...editing, billing_country: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Secteur</Label><Input value={editing.sector} onChange={e => setEditing({ ...editing, sector: e.target.value })} placeholder="boulangerie, avocat…" /></div>
                <div>
                  <Label>Statut</Label>
                  <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["prospect","onboarding","active","paused","archived"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Site URL</Label><Input value={editing.site_url || ""} onChange={e => setEditing({ ...editing, site_url: e.target.value })} placeholder="https://client.fr" /></div>
              <div><Label>Notes internes</Label><Textarea rows={3} value={editing.notes} onChange={e => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving}><Save className="w-4 h-4 mr-1" />{saving ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.company_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              {/* KPIs agrégés multi-sites */}
              {selected.aggregate && (
                <div className="grid grid-cols-3 gap-2">
                  <Card className="p-3 text-center" data-testid="client-aggregate-sites">
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Sites</div>
                    <div className="text-2xl font-bold">{selected.aggregate.sites_count}</div>
                    <div className="text-[10px] text-emerald-600">{selected.aggregate.active_sites} actifs</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">MRR</div>
                    <div className="text-2xl font-bold">{cents(selected.aggregate.total_mrr_cents)}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">par mois</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Suspendus</div>
                    <div className="text-2xl font-bold text-red-500">{selected.aggregate.suspended_sites}</div>
                  </Card>
                </div>
              )}

              <div>
                <div className="text-xs uppercase text-[hsl(var(--muted-foreground))] mb-1">Contact</div>
                <div>{selected.contact_first_name} {selected.contact_last_name}</div>
                <div>{selected.contact_email} · {selected.contact_phone}</div>
                {selected.billing_address && (
                  <div className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">{selected.billing_address}, {selected.billing_zip} {selected.billing_city}</div>
                )}
              </div>

              {/* Sprint F1 — Liste des sites */}
              <ClientSitesPanel client={selected} onChange={async () => {
                const r = await api.get(`/agency/clients/${selected.id}`);
                setSelected(r.data);
              }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =========================================================
// CLIENT SITES PANEL (Sprint F1 — multi-sites par client)
// =========================================================
function ClientSitesPanel({ client, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ label: "Nouveau site", domain: "", plan_code: "", sector_preset: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (showAdd && plans.length === 0) {
      api.get("/agency/pricing-plans").then(r => {
        setPlans(r.data);
        if (r.data.length > 0) setForm(f => ({ ...f, plan_code: r.data[0].code }));
      });
    }
  }, [showAdd, plans.length]);

  const sites = client.sites || [];

  const create = async () => {
    if (!form.label || !form.plan_code) { toast.error("Label + forfait requis"); return; }
    setCreating(true);
    try {
      await api.post(`/agency/clients/${client.id}/sites`, { ...form, client_id: client.id });
      toast.success("Site créé avec subscription dédiée");
      setShowAdd(false); setForm({ label: "Nouveau site", domain: "", plan_code: plans[0]?.code || "", sector_preset: "" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setCreating(false); }
  };

  const removeSite = async (sid) => {
    if (!window.confirm("Supprimer ce site et son abonnement ? Cela ne supprime pas le pod Emergent.")) return;
    await api.delete(`/agency/clients/${client.id}/sites/${sid}`);
    toast.success("Site supprimé"); onChange();
  };

  const regen = async (sid) => {
    const r = await api.post(`/agency/clients/${client.id}/sites/${sid}/regen-pairing-code`);
    toast.success(`Nouveau code: ${r.data.pairing_code}`); onChange();
  };

  const markActive = async (sid) => {
    await api.post(`/agency/clients/${client.id}/sites/${sid}/mark-active`);
    toast.success("Site marqué actif"); onChange();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copié");
  };

  const statusColor = { provisioning: "#A78BFA", active: "#34D399", maintenance: "#F59E0B", suspended: "#EF4444", archived: "#94A3B8" };

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-sky-500" />
          <h4 className="font-bold text-sm">Sites du client ({sites.length})</h4>
        </div>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="add-client-site-btn">
          <Plus className="w-3 h-3 mr-1" />{showAdd ? "Annuler" : "Ajouter un site"}
        </Button>
      </div>

      {showAdd && (
        <div className="border border-dashed rounded-lg p-3 mb-3 space-y-2 bg-sky-500/5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Label interne</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Cabinet Paris" data-testid="new-site-label" />
            </div>
            <div>
              <Label className="text-xs">Domaine final</Label>
              <Input value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="cabinet-paris.fr" data-testid="new-site-domain" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Forfait</Label>
              <select className="w-full h-9 px-2 border rounded text-sm bg-[hsl(var(--background))]" value={form.plan_code} onChange={e => setForm({ ...form, plan_code: e.target.value })} data-testid="new-site-plan">
                {plans.map(p => <option key={p.code} value={p.code}>{p.name} · {(p.monthly_price_cents / 100).toFixed(0)}€/mo</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Preset secteur</Label>
              <Input value={form.sector_preset} onChange={e => setForm({ ...form, sector_preset: e.target.value })} placeholder="boulangerie, avocat..." />
            </div>
          </div>
          <Button size="sm" onClick={create} disabled={creating} data-testid="create-site-confirm-btn" className="w-full">
            {creating ? "Création…" : "Créer le site"}
          </Button>
        </div>
      )}

      {sites.length === 0 && !showAdd && (
        <div className="text-xs text-[hsl(var(--muted-foreground))] text-center py-3">Aucun site — clique 'Ajouter un site' pour commencer.</div>
      )}

      <div className="space-y-2">
        {sites.map(site => {
          const sub = site.subscription || {};
          return (
            <div key={site.id} className="border rounded-lg p-3 bg-white" data-testid={`client-site-${site.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong>{site.label}</strong>
                    <Badge style={{ backgroundColor: statusColor[site.status] || "#94A3B8" }}>{site.status}</Badge>
                    {site.is_paired && <Badge className="bg-emerald-500" title="Site appairé">Appairé</Badge>}
                  </div>
                  {site.domain && <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{site.domain}</div>}
                  {site.pod_url && <a href={site.pod_url} target="_blank" rel="noreferrer" className="text-xs text-sky-500 hover:underline truncate block">{site.pod_url}</a>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                <div className="bg-[hsl(var(--muted))]/40 rounded p-2">
                  <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Abonnement</div>
                  <div className="font-bold">{sub.plan_code || "—"} · <span className={sub.status === "active" ? "text-emerald-600" : "text-amber-600"}>{sub.status || "—"}</span></div>
                  {sub.next_due_at && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Échéance : {(sub.next_due_at || "").slice(0, 10)}</div>}
                </div>
                <div className="bg-[hsl(var(--muted))]/40 rounded p-2">
                  <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Code d'appairage</div>
                  <button type="button" onClick={() => copyCode(site.pairing_code)} className="font-mono font-bold tracking-widest hover:text-sky-500" title="Copier">
                    {site.pairing_code || "—"}
                  </button>
                </div>
              </div>

              <div className="flex gap-1 flex-wrap">
                {!site.is_paired && site.pairing_code && (
                  <Button size="sm" variant="outline" onClick={() => regen(site.id)} className="text-xs">Regen code</Button>
                )}
                {site.status !== "active" && (
                  <Button size="sm" variant="outline" onClick={() => markActive(site.id)} className="text-xs" data-testid={`mark-active-${site.id}`}>Marquer actif</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => removeSite(site.id)} className="text-xs text-red-500 ml-auto"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}




// =========================================================
// ONBOARDING REQUESTS (Sprint 3B — admin view)
// =========================================================
export function AgencyOnboardingRequestsAdmin() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);   // detail row
  const [provisionResult, setProvisionResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/agency/onboarding/requests");
      setRows(r.data);
    } catch { toast.error("Chargement impossible"); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (rid, status) => {
    setLoading(true);
    try {
      await api.put(`/agency/onboarding/requests/${rid}/status`, { status, note: "" });
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const provisionRequest = async (rid) => {
    if (!window.confirm("Provisionner ce client ? Cela crée la fiche client agence + le 1er site avec un code d'appairage.")) return;
    setLoading(true);
    try {
      const r = await api.post(`/agency/onboarding/requests/${rid}/provision`);
      setProvisionResult(r.data);
      toast.success(r.data.already_provisioned ? "Déjà provisionné" : "Client provisionné — voir la checklist");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const openDetail = async (rid) => {
    try {
      const r = await api.get(`/agency/onboarding/requests/${rid}/installments`);
      setSelected(r.data);
    } catch { toast.error("Chargement détail impossible"); }
  };

  const statusColor = {
    pending: "#F59E0B", paid_setup: "#60A5FA", provisioning: "#A78BFA",
    active: "#34D399", refused: "#EF4444",
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-onboarding-requests-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Demandes d'onboarding</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Clients ayant rempli le formulaire public `/onboarding` — CGV signées + setup fees payés.</p>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-xs uppercase">
            <tr>
              <th className="text-left p-3">Entreprise</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Forfait</th>
              <th className="text-left p-3">Setup</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center p-10 text-[hsl(var(--muted-foreground))]">Aucune demande</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-[hsl(var(--muted))]/30" data-testid={`onboarding-row-${r.id}`}>
                <td className="p-3 font-medium">{r.company_name}</td>
                <td className="p-3">
                  <div>{r.contact_first_name} {r.contact_last_name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{r.contact_email}</div>
                </td>
                <td className="p-3">{r.plan_code}</td>
                <td className="p-3">
                  <div>{cents(r.setup_fee_total_cents)} en {r.setup_installments}×</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {r.setup_payment_method === "stripe" ? "Stripe" : "Virement"} · {r.setup_fee_installments_paid || 0}/{r.setup_installments} payées
                  </div>
                </td>
                <td className="p-3"><Badge style={{ backgroundColor: statusColor[r.status] || "#94A3B8" }}>{r.status}</Badge></td>
                <td className="p-3 flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openDetail(r.id)}>Détail</Button>
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "paid_setup")} disabled={loading}>Marquer payé</Button>
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(r.id, "refused")} disabled={loading}>Refuser</Button>
                    </>
                  )}
                  {r.status === "paid_setup" && (
                    <Button size="sm" variant="default" onClick={() => provisionRequest(r.id)} disabled={loading} data-testid={`provision-${r.id}`} className="bg-violet-500 hover:bg-violet-600">
                      <Rocket className="w-3 h-3 mr-1" />Provisionner
                    </Button>
                  )}
                  {r.status === "provisioning" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "active")} disabled={loading}>Activer</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{selected?.company_name} — Échéances setup</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Total : <strong>{cents(selected.setup_fee_total_cents)}</strong></span>
                <span>Payé : <strong className="text-emerald-600">{cents(selected.setup_fee_paid_cents)}</strong></span>
                <Badge style={{ backgroundColor: statusColor[selected.status] }}>{selected.status}</Badge>
              </div>
              <div className="space-y-2">
                {selected.schedule.map(it => (
                  <Card key={it.number} className="p-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">Échéance {it.number}/{selected.setup_installments}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">Due : {(it.due_at || "").slice(0, 10)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{cents(it.amount_cents)}</div>
                      <Badge className={it.status === "paid" ? "bg-emerald-500" : "bg-amber-500"}>{it.status === "paid" ? "Payée" : "En attente"}</Badge>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sprint D semi-auto — Résultat du provisioning */}
      <Dialog open={!!provisionResult} onOpenChange={o => !o && setProvisionResult(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-violet-500" />
              {provisionResult?.already_provisioned ? "Déjà provisionné" : "Client provisionné — étapes suivantes"}
            </DialogTitle>
          </DialogHeader>
          {provisionResult && (
            <div className="space-y-4">
              <Card className="p-4 bg-violet-500/5 border-violet-500/30">
                <div className="text-xs uppercase tracking-wider text-violet-700 font-bold mb-2">Code d'appairage généré</div>
                <div className="flex items-center gap-3">
                  <code className="text-2xl font-mono font-bold tracking-[0.3em] text-violet-900">
                    {provisionResult.pairing_code || provisionResult.site?.pairing_code}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(provisionResult.pairing_code || provisionResult.site?.pairing_code);
                    toast.success("Code copié");
                  }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-violet-700 mt-2">
                  Le client devra coller ce code dans <code>/admin/agency-pairing</code> sur son nouveau pod pour finaliser l'appairage.
                </p>
              </Card>

              <div>
                <h3 className="font-bold text-sm mb-2">Checklist 7 étapes pour finaliser</h3>
                <div className="space-y-1.5">
                  {(provisionResult.next_steps || []).map((step, i) => (
                    <div key={i} className="flex gap-2 items-start text-sm p-2 rounded hover:bg-slate-50">
                      <input type="checkbox" className="mt-1" />
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Card className="p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Client créé</div>
                <div className="font-bold">{provisionResult.client?.company_name}</div>
                <div className="text-xs text-slate-500">id: {provisionResult.client?.id?.slice(0, 12)}…</div>
                {provisionResult.site && (
                  <>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-2 mb-1">Site créé</div>
                    <div className="text-xs">label: {provisionResult.site.label} · plan: {provisionResult.site.plan_code}</div>
                  </>
                )}
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
